import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PageHeader, SegmentedControl } from "@/components/padroes";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileBarChart2, Loader2 } from "lucide-react";
import { triggerDownload } from "@/lib/download";
import type { Pagina } from "@/hooks/usePaginado";

type Modo = "competencia" | "data";

const Relatorio = () => {
  const [modo, setModo] = useState<Modo>("competencia");
  const [mes, setMes] = useState("");
  const [data, setData] = useState("");
  const [loading, setLoading] = useState(false);
  /** Prévia: quantas NFs aprovadas entram no CSV (null = ainda não consultado). */
  const [previa, setPrevia] = useState<number | null>(null);
  const [carregandoPrevia, setCarregandoPrevia] = useState(false);

  const valor = modo === "competencia" ? mes : data;
  const filtro = modo === "competencia" ? `mesReferencia=${mes}` : `data=${data}`;

  // A listagem paginada devolve o total; uma página de 1 item basta para a contagem.
  useEffect(() => {
    setPrevia(null);
    if (!valor) return;
    let ativo = true;
    setCarregandoPrevia(true);
    const t = setTimeout(() => {
      api
        .get<Pagina<unknown>>(`/nfs?page=1&pageSize=1&status=Aprovada&${filtro}`)
        .then((p) => ativo && setPrevia(p.total))
        .catch(() => ativo && setPrevia(null))
        .finally(() => ativo && setCarregandoPrevia(false));
    }, 300);
    return () => {
      ativo = false;
      clearTimeout(t);
    };
  }, [valor, filtro]);

  const handleExport = async () => {
    if (!valor) return;
    setLoading(true);
    try {
      const query = modo === "competencia" ? `mes=${mes}` : `data=${data}`;
      const { blob, filename } = await api.getFile(`/nfs/relatorio-csv?${query}`);
      triggerDownload(blob, filename);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao gerar relatório");
    } finally {
      setLoading(false);
    }
  };

  return (
    <RoleGuard role={["rh", "financeiro"]}>
      <AppLayout title="Relatório">
        <div className="mx-auto flex max-w-[760px] flex-col gap-4">
          <PageHeader
            titulo="Relatórios"
            subtitulo="Exporte as notas fiscais em CSV para conferência ou contabilidade."
          />
          <section className="flex flex-col gap-5 rounded-xl border bg-card p-6">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-primary/10 text-primary">
                <FileBarChart2 className="h-5 w-5" />
              </span>
              <div className="flex flex-col gap-0.5">
                <h3 className="text-base font-semibold">Exportar Relatório CSV</h3>
                <span className="text-[13px] text-muted-foreground">
                  CNPJ, número e valor de todas as notas <strong className="font-semibold">aprovadas</strong> da
                  competência ou da data de envio selecionada.
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-[13px] font-medium">Filtrar por</span>
              <SegmentedControl<Modo>
                aria-label="Filtrar por"
                value={modo}
                onChange={setModo}
                className="grid grid-cols-2 [&>button]:h-8 [&>button]:text-[13px]"
                options={[
                  { value: "competencia", label: "Competência" },
                  { value: "data", label: "Data" },
                ]}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[13px] font-medium" htmlFor="periodo-relatorio">
                {modo === "competencia" ? "Competência" : "Data de envio"}
              </label>
              {modo === "competencia" ? (
                <Input
                  key="mes"
                  id="periodo-relatorio"
                  type="month"
                  value={mes}
                  onChange={(e) => setMes(e.target.value)}
                />
              ) : (
                <Input
                  key="data"
                  id="periodo-relatorio"
                  type="date"
                  value={data}
                  onChange={(e) => setData(e.target.value)}
                />
              )}
            </div>

            {valor && (
              <div className="flex min-h-[44px] items-center justify-between gap-3 rounded-[10px] bg-muted-soft px-3.5 py-3 text-[13px] tabular-nums">
                {carregandoPrevia ? (
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Contando notas…
                  </span>
                ) : previa === null ? (
                  <span className="text-muted-foreground">Não foi possível contar as notas.</span>
                ) : (
                  <span className="text-foreground/80">
                    <strong className="font-semibold text-foreground">{previa.toLocaleString("pt-BR")}</strong>{" "}
                    {previa === 1 ? "nota aprovada" : "notas aprovadas"}{" "}
                    {modo === "competencia" ? "nesta competência" : "enviadas nesta data"}
                  </span>
                )}
              </div>
            )}

            <Button className="h-[42px] w-full" onClick={handleExport} disabled={!valor || loading}>
              {loading ? <Loader2 className="animate-spin" /> : <Download />}
              {loading ? "Gerando..." : "Baixar CSV"}
            </Button>
          </section>
        </div>
      </AppLayout>
    </RoleGuard>
  );
};

export default Relatorio;
